/**
 * Settlements (`/settlements`, TMMRSL-0031 → 0045, H §6.3 / §7.2): the reseller's monthly
 * settlements (`GET /settlements?from=&to=&currency=`) — one per currency per calendar month,
 * closed on day 2 of the next — with the payable bars per currency, the selected settlement's
 * ledger (margin per line, AI at our price, the by-line summary) and CSV export. Settlements
 * renders this inside the Earlier settlements tab; the payout-method panel stays above the tabs.
 * The route has no status parameter: the status select filters the
 * loaded list (one row per month and currency, so filtering in the client is complete). Each
 * settlement is in its own currency; nothing is added across currencies.
 * `409 PAYOUT_METHOD_NOT_CONFIGURED` → the no-agreement empty state. `/payouts` redirects here.
 */
import { useCallback, useMemo, useState } from 'react';
import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { resellerAdminApi } from '@/api/resellerAdmin';
import { ApiErrorState } from '@/components/ApiErrorState';
import { PeriodPicker } from '@/components/PeriodPicker';
import { ReportScope } from '@/components/ReportScope';
import { useRemote } from '@/components/useRemote';
import { enumParam, intParam, useUrlState, type UrlParam } from '@/components/useUrlState';
import type { PeriodRange } from '@/i18n/periods';
import { NotPricedState } from '@/tenant/NotPricedState';
import { LedgerDrillIn } from './LedgerDrillIn';
import { PayableBars } from './PayableBars';
import { PayoutTable } from './PayoutTable';
import { isPayoutMethodNotConfigured } from './notConfigured';
import { byCurrency, isResellerRow, newestFirst } from './statementMath';
import { hasRetained, splitOf, type ConsolePayoutStatus } from './payoutStatus';
import { FIELD_WIDTH } from '@/theme';

const STATUSES: readonly ConsolePayoutStatus[] = [
  'PENDING',
  'CARRIED',
  'PAID',
  'CHARGED',
  'RETAINED',
];
const ALL = '';

/** An ISO 4217 alphabetic code. Anything else (including `zzz`) reads as "all currencies". */
const currencyParam: UrlParam<string> = {
  defaultValue: '',
  parse: (raw) => (/^[A-Z]{3}$/.test(raw) ? raw : undefined),
  serialize: (value) => value,
};

/**
 * The list in the URL (TMM-B6): `?status=`, `?currency=` and the open settlement (`?settlement=`).
 * Status and currency clear `settlement` in the same write. Opening pushes; Close goes back when
 * that push is still the previous entry, and replaces otherwise (TMMRSL-0074).
 */
const SCHEMA = {
  status: enumParam([ALL, ...STATUSES], ALL),
  currency: currencyParam,
  settlement: intParam(0),
};

export function PayoutsScreen() {
  const { t } = useTranslation();
  const [range, setRange] = useState<PeriodRange | null>(null);
  const url = useUrlState(SCHEMA, { page: 'settlement' });
  const { status, currency } = url.values;
  const [known, setCurrencies] = useState<string[]>([]);
  // A currency from the URL is an option before the list has shown it.
  const currencies = currency && !known.includes(currency) ? [...known, currency].sort() : known;

  const rememberCurrencies = useCallback((seen: string[]) => {
    setCurrencies((known) => {
      const next = [...new Set([...known, ...seen])].sort();
      return next.length === known.length ? known : next;
    });
  }, []);

  const period = <PeriodPicker screen="payouts" clears={['settlement']} onChange={setRange} />;
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2.5 }}>{period}</Box>
      <Stack spacing={2.5}>
        <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', rowGap: 1.5 }}>
          <FormControl size="small" sx={{ minWidth: FIELD_WIDTH.sm }}>
            <InputLabel id="payouts-status-label">{t('payouts.filter.status')}</InputLabel>
            <Select
              labelId="payouts-status-label"
              label={t('payouts.filter.status')}
              value={status}
              onChange={(event) => url.set({ status: event.target.value as typeof status })}
            >
              <MenuItem value={ALL}>{t('payouts.filter.allStatuses')}</MenuItem>
              {STATUSES.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`payouts.filter.statuses.${value}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: FIELD_WIDTH.xs }}>
            <InputLabel id="payouts-currency-label">{t('payouts.filter.currency')}</InputLabel>
            <Select
              labelId="payouts-currency-label"
              label={t('payouts.filter.currency')}
              value={currency}
              onChange={(event) => url.set({ currency: event.target.value })}
            >
              <MenuItem value={ALL}>{t('payouts.filter.allCurrencies')}</MenuItem>
              {currencies.map((code) => (
                <MenuItem key={code} value={code}>
                  {code}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        {range ? (
          <StatementsSection
            from={range.from}
            to={range.to}
            status={status || undefined}
            currency={currency || undefined}
            onCurrencies={rememberCurrencies}
            onShowAll={() => url.set({ status: ALL })}
            selectedId={url.values.settlement || undefined}
            openSearch={(id) => url.searchWith({ settlement: id })}
            openState={url.linkState}
            onClose={() => {
              const link = document.getElementById(`settlement-month-${url.values.settlement}`);
              const heading = document.getElementById('payouts-statements-heading');
              const target = link ?? heading;
              if (target instanceof HTMLElement) target.focus();
              url.dismiss('settlement');
            }}
          />
        ) : null}
      </Stack>
    </Box>
  );
}

interface StatementsSectionProps {
  from: string | undefined;
  to: string | undefined;
  status: string | undefined;
  currency: string | undefined;
  onCurrencies: (seen: string[]) => void;
  /** Clears the status filter — the filtered-empty table's "Show all" (STATE-004). */
  onShowAll: () => void;
  /** The open settlement (from the URL). */
  selectedId: number | undefined;
  openSearch: (id: number) => string;
  /** History state for the month link, so Close can go back. */
  openState: unknown;
  /** Closes the ledger. */
  onClose: () => void;
}

function StatementsSection({
  from,
  to,
  status,
  currency,
  onCurrencies,
  onShowAll,
  selectedId,
  openSearch,
  openState,
  onClose,
}: StatementsSectionProps) {
  const { t } = useTranslation();
  const load = useCallback(async () => {
    const list = await resellerAdminApi.listSettlements({ from, to, currency });
    const statements = newestFirst(list.payoutStatements.filter(isResellerRow));
    // The currency filter offers every currency the reseller has been seen to earn in.
    onCurrencies(statements.map((stmt) => stmt.currency).filter(Boolean));
    return statements;
  }, [from, to, currency, onCurrencies]);
  const remote = useRemote(load);
  // `/settlements` takes no status: the select narrows the loaded list, which is complete (one row
  // per month and currency).
  const statements = useMemo(
    () =>
      (remote.data ?? []).filter((stmt) => {
        if (!status) return true;
        // A split month has no status of its own: Retained matches its retained part, and any
        // other status matches its normal part.
        if (status === 'RETAINED') return hasRetained(stmt);
        return stmt.payoutStatus === status || splitOf(stmt)?.normal.status === status;
      }),
    [remote.data, status],
  );
  const selected = statements.find((stmt) => stmt.id === selectedId);

  if (remote.status === 'error' && isPayoutMethodNotConfigured(remote.error)) {
    return <NotPricedState />;
  }
  if (remote.status === 'error') {
    return <ApiErrorState what={t('errors.what.settlements')} error={remote.error} onRetry={remote.reload} />;
  }
  if (!remote.data) {
    return (
      <Box role="status" aria-label={t('common.loading')}>
        <Skeleton variant="rounded" height={240} />
      </Box>
    );
  }

  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography
          id="payouts-statements-heading"
          variant="subtitle1"
          component="h2"
          tabIndex={-1}
          sx={{ '&:focus:not(:focus-visible)': { outline: 'none' } }}
        >
          {t('payouts.statementsTitle')}
        </Typography>
        {/* The requested range (settlements carry their own dates per row), or the preset's label. */}
        <ReportScope from={from} to={to} />
      </Box>
      {statements.length > 0 ? (
        <Grid container spacing={2}>
          {byCurrency(statements).map(([code, group]) => (
            <Grid key={code || 'none'} size={{ xs: 12, lg: 6 }}>
              <PayableBars statements={group} currency={code} />
            </Grid>
          ))}
        </Grid>
      ) : null}
      <PayoutTable
        statements={statements}
        selectedId={selectedId}
        openSearch={openSearch}
        openState={openState}
        onShowAll={status ? onShowAll : undefined}
      />
      {selected ? <LedgerDrillIn statement={selected} onClose={onClose} /> : null}
      {!selected && selectedId ? (
        <Typography role="status">{t('payouts.settlementNotInView')}</Typography>
      ) : null}
    </Stack>
  );
}
